import { PayoutEntryHedge } from '../../../state/Radom'
import Treasury from '../../../state/Treasury'
import User from '../../../state/User'
import { formatCurrency } from '../../../util/Util'
import { HedgedRouteInfo } from './payoutTypes'

export const toCostNumber = (value: number | string | undefined): number => {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : 0
}

export const fiatPriceForTicker = (ticker: string): number | undefined => {
  const defaultCurrency = User.organization?.defaultCurrency ?? 'USD'
  const upper = ticker.toUpperCase()
  if (upper === defaultCurrency.toUpperCase()) return 1
  if (defaultCurrency.toUpperCase() === 'USD' && ['USDC', 'USDT', 'USD'].includes(upper)) return 1
  const priceQuote = Treasury.priceQuotes.find(p => p.from.toLowerCase() === ticker.toLowerCase())
  const value = priceQuote ? Number(priceQuote.value) : NaN
  return Number.isFinite(value) && value > 0 ? value : undefined
}

export const formatFiatCost = (value: number, currency?: string): string => {
  const fiatCurrency = currency ?? User.organization?.defaultCurrency ?? 'USD'
  return value > 0 && value < 0.01
    ? `< ${formatCurrency(0.01, fiatCurrency)}`
    : formatCurrency(value, fiatCurrency)
}

export const withFiatCost = (base: string, cost: number | undefined, currency?: string): string =>
  cost == null ? base : `${base} · ≈ ${formatFiatCost(cost, currency)}`

export const costFromBps = (notional: number | null | undefined, bps: number): number | undefined => {
  if (notional == null || !Number.isFinite(notional) || notional <= 0) return undefined
  return notional * (bps / 10000)
}

export interface HedgedPayoutCostParts {
  lockedRate: number
  midPrice: number
  markupBps: number
  rateCost: number | undefined
  conversionFee: number
  conversionFeePercent: number
  conversionFeeCost: number | undefined
  feeBuffer: number
  feeBufferCost: number | undefined
  providerWithdrawalFee: number
  providerFeeCost: number | undefined
  stableForTrade: number
  stablecoinCost: number
  totalCost: number | undefined
}

export const computeHedgedPayoutCosts = (hedge: PayoutEntryHedge): HedgedPayoutCostParts => {
  const lockedRate = toCostNumber(hedge.lockedRate)
  const midPrice = toCostNumber(hedge.midPrice)
  const stableForTrade = toCostNumber(hedge.stableForTrade)
  const conversionFee = toCostNumber(hedge.conversionFee)
  const feeBuffer = toCostNumber(hedge.feeBuffer)
  const providerWithdrawalFee = toCostNumber(hedge.providerWithdrawalFee)
  const stablecoinCost = toCostNumber(hedge.stablecoinCost)
  const fundingPrice = fiatPriceForTicker(hedge.fundingCurrency)

  const markupBps = midPrice > 0
    ? Math.max(0, ((lockedRate - midPrice) / midPrice) * 10000)
    : 0
  const rateCostFunding = lockedRate > 0
    ? Math.max(0, stableForTrade * (1 - midPrice / lockedRate))
    : 0
  const feeBufferFunding = feeBuffer * lockedRate
  const providerFeeFunding = providerWithdrawalFee * lockedRate

  return {
    lockedRate,
    midPrice,
    markupBps,
    rateCost: fundingPrice != null ? rateCostFunding * fundingPrice : undefined,
    conversionFee,
    conversionFeePercent: stableForTrade > 0 ? (conversionFee / stableForTrade) * 100 : 0,
    conversionFeeCost: fundingPrice != null ? conversionFee * fundingPrice : undefined,
    feeBuffer,
    feeBufferCost: fundingPrice != null ? feeBufferFunding * fundingPrice : undefined,
    providerWithdrawalFee,
    providerFeeCost: fundingPrice != null ? providerFeeFunding * fundingPrice : undefined,
    stableForTrade,
    stablecoinCost,
    totalCost: fundingPrice != null ? stablecoinCost * fundingPrice : undefined
  }
}

export const estimatedRouteCosts = (
  notionalFunding: number | null | undefined,
  route: HedgedRouteInfo,
  fundingTicker: string
): { slippage: number | undefined, hedging: number | undefined } => {
  const fundingPrice = fiatPriceForTicker(fundingTicker) ?? 1
  const notionalFiat = notionalFunding != null && notionalFunding > 0
    ? notionalFunding * fundingPrice
    : null
  return {
    slippage: costFromBps(notionalFiat, route.estimatedExecutionFeeBps),
    hedging: costFromBps(notionalFiat, route.estimatedHedgingBps)
  }
}
